// Die Arbeitsbereiche als Daten. App.tsx rendert daraus ein Gerüst - Bereichsumschalter, Menü, Reiter,
// Frage an den Assistenten -, ohne einen Bereich beim Namen zu kennen. Ein neuer Bereich ist ein
// Eintrag hier, seine Routen in App.tsx und seine Seiten unter src/seiten/<bereich>/.

import { notierungenApi } from './api_notierungen'
import type { Benutzer, BereichId } from './typen'

/** Welche Zahl ein Menüpunkt oder Reiter als Marke trägt; App.tsx holt sie für den aktiven Bereich. */
export type Zaehler = 'rueckfragen' | 'notierungen_pruefen'

export interface MenuePunkt {
  pfad: string
  titel: string
  /** aktiv auch auf diesen Pfaden und darunter - „Kladde" gehört zur Disposition */
  aktivAuf: string[]
  /** Stammdaten stehen abgesetzt: was man pflegt, nicht was man täglich tut */
  abgesetzt?: boolean
  zaehler?: Zaehler
}

export interface ReiterVorlage {
  pfad: string
  titel: string
  zaehler?: Zaehler
  /** nur genau dieser Pfad macht den Reiter aktiv (der erste Reiter einer Gruppe mit Unterpfaden) */
  ende?: boolean
}

export interface ReiterGruppe {
  /** die Pfade, unter denen diese Reiterzeile steht */
  aktivAuf: string[]
  eintraege?: ReiterVorlage[]
  /** Reiter, die erst das Backend kennt - die Gruppen der Notierungstabelle */
  laden?: () => Promise<ReiterVorlage[]>
}

export interface Bereich {
  id: BereichId
  titel: string
  /** ein Wort für enge Stellen (Spalte „Bereich" der Mails, Knopf „zu Verpackung") */
  kurztitel: string
  /** ein Satz, was der Bereich ist - steht auf der Kachel der Bereichsauswahl */
  kurztext: string
  startpfad: string
  /** alle Pfade des Bereichs beginnen damit; leer = alles, was keinem anderen Bereich gehört */
  praefix: string
  menue: MenuePunkt[]
  reiter: ReiterGruppe[]
  assistent: {
    /** Platzhalter der Eingabe - je ein Beispiel, was man sagen kann */
    platzhalter: string
    /** was der Assistent dieses Bereichs kann, unter der Gesprächsliste */
    kann: string
    /** der Knopf rechts unter der Eingabe */
    zurSeite: { pfad: string; titel: string }
  }
}

/** Liegt der Pfad auf oder unter einem der genannten Pfade? „/importe" deckt auch „/importe/3" ab. */
export function liegtUnter(pfad: string, basen: string[]): boolean {
  return basen.some((b) => pfad === b || pfad.startsWith(`${b}/`))
}

const VERPACKUNG: Bereich = {
  id: 'verpackung',
  titel: 'Verpackungsdisposition',
  kurztitel: 'Verpackung',
  kurztext: 'Bestände, Reichweiten und Bestellungen der Verpackungsmaterialien - die Kladde von Frau Barth.',
  startpfad: '/assistent',
  praefix: '',
  // Sieben Wörter im Kopf, in der Reihenfolge des Arbeitstags: der Assistent geht die offenen Punkte
  // durch, dann was heute ansteht, disponieren, bestellen, zählen, freigeben, Stammdaten pflegen.
  menue: [
    { pfad: '/assistent', titel: 'Assistent', aktivAuf: ['/assistent'] },
    { pfad: '/heute', titel: 'Heute', aktivAuf: ['/heute', '/faellig'] },
    { pfad: '/disposition', titel: 'Disposition', aktivAuf: ['/disposition', '/kladde', '/forecast'] },
    { pfad: '/bestellen', titel: 'Bestellen', aktivAuf: ['/bestellen'] },
    { pfad: '/inventur', titel: 'Inventur', aktivAuf: ['/inventur'] },
    { pfad: '/importe', titel: 'Importe', aktivAuf: ['/importe', '/rueckfragen', '/erfassen'], zaehler: 'rueckfragen' },
    { pfad: '/stammdaten', titel: 'Stammdaten', aktivAuf: ['/stammdaten', '/lieferanten', '/regeln', '/einstellungen'], abgesetzt: true },
  ],
  // Bestellen, Inventur und Assistent haben keine Reiter.
  reiter: [
    {
      aktivAuf: ['/disposition', '/kladde', '/forecast'],
      eintraege: [
        { pfad: '/disposition', titel: 'Kladde Material' },
        { pfad: '/kladde', titel: 'Zu- und Abgänge' },
        { pfad: '/forecast', titel: 'Forecast und Plan-Ist' },
      ],
    },
    {
      aktivAuf: ['/importe', '/rueckfragen', '/erfassen'],
      eintraege: [
        { pfad: '/importe', titel: 'Läufe' },
        { pfad: '/rueckfragen', titel: 'Rückfragen', zaehler: 'rueckfragen' },
        { pfad: '/erfassen', titel: 'Von Hand erfassen' },
      ],
    },
    {
      aktivAuf: ['/stammdaten', '/lieferanten', '/regeln', '/einstellungen'],
      eintraege: [
        { pfad: '/stammdaten', titel: 'Artikel' },
        { pfad: '/lieferanten', titel: 'Lieferanten' },
        { pfad: '/regeln', titel: 'Regeln' },
        { pfad: '/einstellungen', titel: 'Einstellungen' },
      ],
    },
  ],
  assistent: {
    platzhalter:
      'Frage oder Auftrag - zum Beispiel: Was ist bei 930270 zu klären? Setze den Forecast für Sanna Würfel im Oktober auf 60 t.',
    kann: 'Der Assistent kann alles, was die Seiten können: nachsehen, planen, Importe prüfen und freigeben, Inventur, Lieferanten, Regeln. Buchen, Entscheiden und Löschen fragt er per Knopf.',
    zurSeite: { pfad: '/disposition', titel: 'Zur Disposition' },
  },
}

const N = '/notierungen'

const NOTIERUNGEN: Bereich = {
  id: 'notierungen',
  titel: 'Rohstoffnotierungen',
  kurztitel: 'Notierungen',
  kurztext: 'Tagespreise der Öl-Lieferanten wie im Excel-Vergleich - mit Einkaufssignal, wenn ein Preis deutlich fällt.',
  startpfad: `${N}/heute`,
  praefix: N,
  // Herr Schulz schaut zuerst, ob heute Einkaufstag ist, dann in die Tabelle; Frau Geigenmüller prüft
  // die Läufe, die nicht von selbst übernommen wurden.
  menue: [
    { pfad: `${N}/assistent`, titel: 'Assistent', aktivAuf: [`${N}/assistent`] },
    { pfad: `${N}/heute`, titel: 'Heute', aktivAuf: [`${N}/heute`] },
    { pfad: `${N}/tabelle`, titel: 'Tabelle', aktivAuf: [`${N}/tabelle`] },
    { pfad: `${N}/verlauf`, titel: 'Verlauf', aktivAuf: [`${N}/verlauf`] },
    { pfad: `${N}/importe`, titel: 'Importe', aktivAuf: [`${N}/importe`, `${N}/erfassen`, `${N}/eingaenge`], zaehler: 'notierungen_pruefen' },
    { pfad: `${N}/stammdaten`, titel: 'Stammdaten', aktivAuf: [`${N}/stammdaten`], abgesetzt: true },
  ],
  reiter: [
    {
      aktivAuf: [`${N}/tabelle`],
      // Je Gruppe ein Reiter - wie je Excel-Mappe ein Fenster.
      laden: async () =>
        (await notierungenApi.gruppen()).map((g) => ({ pfad: `${N}/tabelle/${encodeURIComponent(g.gruppe)}`, titel: g.gruppe })),
    },
    {
      aktivAuf: [`${N}/importe`, `${N}/erfassen`, `${N}/eingaenge`],
      eintraege: [
        { pfad: `${N}/importe`, titel: 'Läufe', zaehler: 'notierungen_pruefen' },
        { pfad: `${N}/erfassen`, titel: 'Von Hand erfassen' },
        { pfad: `${N}/eingaenge`, titel: 'Eingänge' },
      ],
    },
    {
      aktivAuf: [`${N}/stammdaten`],
      eintraege: [
        { pfad: `${N}/stammdaten`, titel: 'Spalten', ende: true },
        { pfad: `${N}/stammdaten/regeln`, titel: 'Regeln' },
        { pfad: `${N}/stammdaten/lieferanten`, titel: 'Lieferanten' },
        { pfad: `${N}/stammdaten/rohstoffe`, titel: 'Rohstoffe' },
        { pfad: `${N}/stammdaten/signale`, titel: 'Signale' },
      ],
    },
  ],
  assistent: {
    platzhalter:
      'Frage oder Auftrag - zum Beispiel: Ist heute Einkaufstag für Palmöl? Wer war im September für Rapsöl am günstigsten?',
    kann: 'Der Assistent kennt alle Notierungen: Preise vergleichen, Verläufe zeigen, Läufe prüfen und übernehmen, Spalten und Zuordnungsregeln pflegen. Übernehmen, Verwerfen und Löschen fragt er per Knopf.',
    zurSeite: { pfad: `${N}/tabelle`, titel: 'Zur Tabelle' },
  },
}

/** Alle Bereiche in der Reihenfolge der Bereichsauswahl. */
export const BEREICHE: Bereich[] = [VERPACKUNG, NOTIERUNGEN]

export function bereichVon(id: BereichId): Bereich {
  return BEREICHE.find((b) => b.id === id) ?? VERPACKUNG
}

/** Zu welchem Bereich ein Pfad gehört; die Startseite „/" gehört keinem. */
export function bereichFuerPfad(pfad: string): Bereich | null {
  if (pfad === '/') return null
  return (
    BEREICHE.find((b) => b.praefix !== '' && liegtUnter(pfad, [b.praefix])) ??
    BEREICHE.find((b) => b.praefix === '') ??
    null
  )
}

/** Was der Benutzer öffnen darf. Kennt das Backend die Rechte noch nicht (Feld fehlt), gilt alles. */
export function erlaubteBereiche(benutzer: Pick<Benutzer, 'bereiche'>): BereichId[] {
  const ids = BEREICHE.map((b) => b.id)
  if (!benutzer.bereiche || benutzer.bereiche.length === 0) return ids
  return ids.filter((id) => benutzer.bereiche!.includes(id))
}
